/* eslint-disable no-shadow */
import { loadAsyncComponent } from '@fifa-bsm/bo-frontend-shared/core';
import {
    assignSwappableSeats,
    buildDataForBulkSwap,
    store as bulkSwapSeatsSharedModule,
} from '@/app/store/seatAllocation/shared/bulkSwapSeats';
import { isTicketDistributedOrBeingDistributed } from '@/app/modules/seating/guestOperations/Picker/domain/tickets/tickets';
import {
    toggleElementsOnList,
    uniqueBy,
    UniqueByStrategy,
} from '@fifa-bsm/bo-frontend-shared/utils';
import { guestOperationsSeatAllocationApi } from '@/app/shared/services/apis/guestOperationsSeatAllocationApi.service';
import { pipe } from '@fifa-bsm/bo-frontend-shared/utils';
import { dispatchShowBackdropComponent } from '@fifa-bsm/bo-frontend-shared/components/AppBackdrop/showBackdropComponent';
import { getGuestName } from '@/app/modules/seating/guestOperations/Picker/domain/getGuestName';
import { t } from '@/i18n';
import { isModalCancelled } from '@fifa-bsm/bo-frontend-shared/components/AppBackdrop/ModalCancelled';

const getters = {
    ...bulkSwapSeatsSharedModule.getters({
        canPerform(state) {
            return (
                state.selectedActiveItems.length ===
                state.selectedEmptySeatIds.length + state.itemsToSwap.length
            );
        },
        canActivate(state, getters) {
            return getters.eligibleItems.length >= 2 && !state.active;
        },
    }),
    eligibleItems(state, getters, rootState, rootGetters) {
        return filterTicketsEligibleForSwap(
            rootGetters['SeatAllocation/guestOperations/TicketList/notProcessedActiveTickets']
        );
    },
};

const filterTicketsEligibleForSwap = (tickets) => tickets.filter(canAddSwapItemToBulkSwap);
const canAddSwapItemToBulkSwap = (ticket) =>
    !isTicketDistributedOrBeingDistributed(ticket) && ticket.seat;

const actionsShared = {
    ...bulkSwapSeatsSharedModule.actions({
        async swapSeats({ state, rootGetters, rootState, commit, dispatch }) {
            const {
                activeItemsByRowId,
                swappableSeatsByRowId,
                activeItemsSeatsByRowId,
                itemsToSwapByRowId,
            } = buildDataForBulkSwap({
                selectedActiveItems: state.selectedActiveItems,
                itemsToSwap: state.itemsToSwap,
                selectedEmptySeatIds: state.selectedEmptySeatIds,
                seatsMap: rootGetters['SeatAllocation/guestOperations/Picker/seatsMap'],
            });

            const leftSideSwapAssignments = assignSwappableSeats({
                itemsToSwapByRowId: activeItemsByRowId,
                swappableSeatsByRowId: swappableSeatsByRowId,
            });

            const rightSideSwapAssignments = assignSwappableSeats({
                itemsToSwapByRowId: itemsToSwapByRowId,
                swappableSeatsByRowId: activeItemsSeatsByRowId,
                alreadyAssignedSeatIds: leftSideSwapAssignments.map(
                    (assignment) => assignment.seat.id
                ),
                discardCallback: (item) =>
                    leftSideSwapAssignments.some(
                        (leftSideAssignment) => leftSideAssignment.item.id === item.id
                    ),
            });

            const allAssignments = pipe((assignments) =>
                uniqueBy((assignment) => assignment.item.id, assignments, {
                    strategy: UniqueByStrategy.BY_FIRST_MATCHING,
                })
            )([...leftSideSwapAssignments, ...rightSideSwapAssignments]);

            const modalResult = await dispatchShowBackdropComponent(dispatch, {
                component: loadAsyncComponent(
                    () =>
                        import(
                            '@/app/modules/seating/shared/components/BulkSwapSeatsConfirmationModal/BulkSwapSeatsConfirmationModal.vue'
                        )
                ),
                props: {
                    assignments: allAssignments.map((assignment) => ({
                        item: {
                            name: getGuestName(assignment.item.guest, false),
                            seat: {
                                ...assignment.item.seat,
                                category: assignment.item.seat.category.name,
                            },
                        },
                        seat: { ...assignment.seat, category: assignment.seat.category.name },
                    })),
                    headerText: t('lang_seating.modal.bulkSwapSeats.SWAP_GUESTS'),
                    infoText: t(
                        'lang_seating.modal.bulkSwapSeats.FOLLOWING_GUESTS_WILL_BE_SWAPPED'
                    ),
                    confirmationButtonText: t('lang_seating.modal.bulkSwapSeats.SWAP_GUESTS'),
                },
            });

            if (isModalCancelled(modalResult)) {
                return;
            }

            const ticketIds = [
                ...allAssignments.map((assignment) => assignment.id),
                ...[...state.selectedActiveItems, ...state.itemsToSwap]
                    .map((item) => item?.id)
                    .filter(Boolean),
            ];

            const seatIds = [
                ...allAssignments.map((assignment) => assignment.seat.id),
                ...[...state.selectedActiveItems, ...state.itemsToSwap]
                    .map((item) => item?.seat.id)
                    .filter(Boolean),
            ];

            dispatch(
                'SeatAllocation/guestOperations/Picker/addToProcessed',
                { seatIds, ticketIds },
                {
                    root: true,
                }
            );

            try {
                await guestOperationsSeatAllocationApi.deallocateMultipleSeats(
                    rootGetters['SeatAllocation/guestOperations/Picker/eventId'],
                    rootGetters['SeatAllocation/guestOperations/Picker/currentGOSeatPlanId'],
                    {
                        undoDistribution: false,
                        lastMinuteChanges:
                            rootState[
                                'SeatAllocation/guestOperations/PickerTopBar/lastMinuteChanges'
                            ],
                        ticketIds: [...state.selectedActiveItems, ...state.itemsToSwap].map(
                            (item) => item.id
                        ),
                    }
                );

                await guestOperationsSeatAllocationApi.allocateSeats(
                    rootGetters['SeatAllocation/guestOperations/Picker/eventId'],
                    rootGetters['SeatAllocation/guestOperations/Picker/currentGOSeatPlanId'],
                    allAssignments.map((assignment) => {
                        return { ticket_id: assignment.item.id, venue_seat_id: assignment.seat.id };
                    })
                );

                await dispatch(
                    'SeatAllocation/guestOperations/Picker/refreshTicketsList',
                    {
                        ticketIds: allAssignments.map((assignment) => assignment.item.id),
                    },
                    { root: true }
                );

                dispatch(
                    'SeatAllocation/guestOperations/Picker/removeFromProcessed',
                    { seatIds, ticketIds },
                    {
                        root: true,
                    }
                );

                commit('deactivate');
            } catch (error) {
                await guestOperationsSeatAllocationApi.allocateSeats(
                    rootGetters['SeatAllocation/guestOperations/Picker/eventId'],
                    rootGetters['SeatAllocation/guestOperations/Picker/currentGOSeatPlanId'],
                    pipe(
                        (items) =>
                            items.map((item) => {
                                return { ticket_id: item.id, venue_seat_id: item.seat.id };
                            }),
                        (items) => uniqueBy((item) => item.ticket_id, items)
                    )([...state.selectedActiveItems, ...state.itemsToSwap])
                );

                await dispatch(
                    'SeatAllocation/guestOperations/Picker/refreshTicketsList',
                    {
                        withLoading: true,
                    },
                    { root: true }
                );

                commit(
                    'SeatAllocation/guestOperations/Picker/removeSeatIdsFromProcessed',
                    seatIds,
                    { root: true }
                );

                throw error;
            }
        },
        onActivate: ({ getters, commit }) => {
            commit(
                'SeatAllocation/guestOperations/TicketList/setTicketsActiveForAllocation',
                getters.eligibleItems,
                { root: true }
            );
            commit(
                'SeatAllocation/guestOperations/Picker/setHighlightedSeatIdsWithoutAllocation',
                [],
                { root: true }
            );
        },
    }),
};

const actions = {
    ...actionsShared,
};

const mutations = {
    ...bulkSwapSeatsSharedModule.mutations,
    setItemsToSwap(state, items) {
        state.itemsToSwap = filterTicketsEligibleForSwap(items);
    },
    toggleItemToSwap(state, item) {
        if (!canAddSwapItemToBulkSwap(item)) {
            return;
        }
        state.itemsToSwap = toggleElementsOnList(state.itemsToSwap, [item]);
    },
};

export default {
    namespaced: true,
    state: bulkSwapSeatsSharedModule.state,
    getters,
    actions,
    mutations,
};
